import {memo, useCallback, useEffect, useRef, useState, type TouchEvent} from 'react'
import {RoomEvent, Track} from 'livekit-client'
import {useTracks, useVisualStableUpdate} from '@livekit/components-react'
import type {TrackReferenceOrPlaceholder} from '@livekit/components-react'
import {useGridLayout} from './useGridLayout'
import {VideoTile} from './VideoTile'
import {Pagination} from './Pagination'

function getTrackKey(ref: TrackReferenceOrPlaceholder): string {
  return `${ref.participant.identity}|${ref.source}`
}

export const VideoGrid = memo(function VideoGrid() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const touchStartXRef = useRef<number | null>(null)

  const tracks = useTracks(
    [
      {source: Track.Source.Camera, withPlaceholder: true},
      {source: Track.Source.ScreenShare, withPlaceholder: false},
    ],
    {
      updateOnlyOn: [RoomEvent.ActiveSpeakersChanged],
      onlySubscribed: false,
    },
  )

  const isFocusMode = focusedId !== null
  const {cols, rows, itemsPerPage} = useGridLayout(
    containerRef,
    isFocusMode ? 'focus' : 'grid',
    tracks.length,
  )

  const displayTracks = isFocusMode ? tracks.filter(ref => getTrackKey(ref) !== focusedId) : tracks
  const stableTracks = useVisualStableUpdate(displayTracks, itemsPerPage)

  const totalPages = Math.max(1, Math.ceil(stableTracks.length / Math.max(1, itemsPerPage)))
  const safePage = Math.min(currentPage, totalPages - 1)
  const pageItems = stableTracks.slice(safePage * itemsPerPage, (safePage + 1) * itemsPerPage)

  const focusedTrackRef = tracks.find(ref => getTrackKey(ref) === focusedId) ?? null

  useEffect(() => {
    if (focusedId !== null && focusedTrackRef === null) {
      setFocusedId(null)
      setCurrentPage(0)
    }
  }, [focusedId, focusedTrackRef])

  const handleSwipeStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null
  }, [])

  const handleSwipeEnd = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (touchStartXRef.current === null) return
      const endX = e.changedTouches[0]?.clientX
      if (endX === undefined) return
      const delta = touchStartXRef.current - endX
      touchStartXRef.current = null
      const SWIPE_THRESHOLD = 50
      if (delta > SWIPE_THRESHOLD && safePage < totalPages - 1) {
        setCurrentPage(safePage + 1)
      } else if (delta < -SWIPE_THRESHOLD && safePage > 0) {
        setCurrentPage(safePage - 1)
      }
    },
    [safePage, totalPages],
  )

  const handleTileClick = useCallback((trackKey: string) => {
    setFocusedId(prev => {
      const isTogglingOff = prev === trackKey
      if (isTogglingOff || prev === null) setCurrentPage(0)
      return isTogglingOff ? null : trackKey
    })
  }, [])

  return (
    <div className="flex min-h-0 flex-1 flex-col p-1">
      <div
        ref={containerRef}
        className="relative min-h-0 flex-1"
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
      >
        {isFocusMode && focusedTrackRef ? (
          <div className="flex h-full flex-col gap-2">
            <div className="min-h-0 flex-3">
              <VideoTile
                trackRef={focusedTrackRef}
                isFocused
                onClick={() => handleTileClick(getTrackKey(focusedTrackRef))}
                className="h-full w-full"
              />
            </div>
            {pageItems.length > 0 && (
              <div
                className="grid min-h-0 flex-1 justify-center gap-2"
                style={{
                  gridTemplateColumns: `repeat(${pageItems.length}, calc((100% - ${(cols - 1) * 8}px) / ${cols}))`,
                }}
              >
                {pageItems.map(ref => (
                  <VideoTile
                    key={getTrackKey(ref)}
                    trackRef={ref}
                    onClick={() => handleTileClick(getTrackKey(ref))}
                    className="h-full w-full"
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          (() => {
            const GRID_GAP = 8
            const fullRowCount = Math.floor(pageItems.length / cols)
            const fullCount = fullRowCount * cols
            const lastRowItems = pageItems.slice(fullCount)
            const hasIncompleteLastRow = lastRowItems.length > 0 && lastRowItems.length < cols
            const tileWidthCalc = `calc((100% - ${(cols - 1) * GRID_GAP}px) / ${cols})`

            return (
              <div
                className="grid h-full gap-2"
                style={{
                  gridTemplateColumns: `repeat(${cols}, 1fr)`,
                  gridTemplateRows: `repeat(${rows}, 1fr)`,
                }}
              >
                {pageItems.slice(0, fullCount).map(ref => (
                  <VideoTile
                    key={getTrackKey(ref)}
                    trackRef={ref}
                    onClick={() => handleTileClick(getTrackKey(ref))}
                    className="h-full w-full"
                  />
                ))}
                {hasIncompleteLastRow && (
                  <div className="flex justify-center gap-2 min-h-0" style={{gridColumn: '1 / -1'}}>
                    {lastRowItems.map(ref => (
                      <div
                        key={getTrackKey(ref)}
                        className="h-full shrink-0"
                        style={{width: tileWidthCalc}}
                      >
                        <VideoTile
                          trackRef={ref}
                          onClick={() => handleTileClick(getTrackKey(ref))}
                          className="h-full w-full"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })()
        )}
      </div>
      <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  )
})
